import React, { useState, FormEvent } from 'react';
import Modal from '../common/Modal';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  website: string;
  industry: string;
  annualGMV: string;
  phone: string;
  title: string;
  integrationPlatform: string;
  returnsManagementPlatform: string;
  returnRate: string;
}

interface RequestDemoProps {
  isOpen: boolean;
  onClose: () => void;
}

const RequestDemo: React.FC<RequestDemoProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    website: '',
    industry: '',
    annualGMV: '',
    phone: '',
    title: '',
    integrationPlatform: '',
    returnsManagementPlatform: '',
    returnRate: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleWebsiteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    // Remove any existing http:// or https:// to prevent duplicates
    value = value.replace(/^(https?:\/\/)/, '');
    
    // Remove www. if present to standardize input
    value = value.replace(/^www\./, '');
    
    // Update the form data with the cleaned value
    setFormData({ ...formData, website: value });
  };

  const formatWebsiteForSubmission = (website: string): string => {
    if (!website) return '';
    
    // Remove any existing protocol and www
    let formatted = website.replace(/^(https?:\/\/)?(www\.)?/, '');
    
    // Add https:// if there's a value
    return formatted ? `https://${formatted}` : '';
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    
    const submissionData = {
      ...formData,
      website: formatWebsiteForSubmission(formData.website)
    };
    
    try {
      // Log the URL we're trying to use
      const scriptUrl = process.env.REACT_APP_GOOGLE_SCRIPT_URL;
      console.log('Attempting to submit to:', scriptUrl);
      
      if (!scriptUrl) {
        throw new Error('Google Script URL is not configured');
      }

      // Create a URLSearchParams object to send data as form data
      const formPayload = new URLSearchParams();
      Object.entries(submissionData).forEach(([key, value]) => {
        formPayload.append(key, value);
      });

      // Log the payload we're sending
      console.log('Submitting data:', Object.fromEntries(formPayload));

      const response = await fetch(scriptUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formPayload.toString()
      });

      // Since we're using no-cors, we won't get a proper response
      // We'll assume success if no error was thrown
      
      // Clear form data
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        website: '',
        industry: '',
        annualGMV: '',
        phone: '',
        title: '',
        integrationPlatform: '',
        returnsManagementPlatform: '',
        returnRate: ''
      });

      // Show success state instead of alert
      setIsSuccess(true);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('There was an error submitting the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsSuccess(false);
    onClose();
  };

  console.log('Script URL from env:', process.env.REACT_APP_GOOGLE_SCRIPT_URL);

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      {isSuccess ? (
        <div className="text-center py-8">
          <div className="mb-6">
            <svg className="mx-auto h-16 w-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">Thank you!</h2>
          <p className="text-lg text-gray-300 mb-8">
            We have received your request and will contact you soon.
          </p>
          <button
            onClick={handleClose}
            className="px-8 py-4 bg-primary hover:bg-primary-dark text-white font-medium rounded-full transition-colors"
          >
            Close
          </button>
        </div>
      ) : (
        <>
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-white mb-4">Request a demo</h2>
            <p className="text-lg text-gray-300">
              Fill out the form below to learn more about how ValetPe can help your business.
            </p>
            {submitError && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500 rounded-lg text-red-500">
                {submitError}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Name & Last Name */}
            <div>
              <label htmlFor="firstName" className="block text-white mb-2">First Name</label>
              <input
                type="text"
                id="firstName"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-dark-light border border-gray-700 text-white focus:outline-none focus:border-primary"
                required
                placeholder="First Name"
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-white mb-2">Last Name</label>
              <input
                type="text"
                id="lastName"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-dark-light border border-gray-700 text-white focus:outline-none focus:border-primary"
                required
                placeholder="Last Name"
              />
            </div>

            {/* Email & Company */}
            <div>
              <label htmlFor="email" className="block text-white mb-2">Email Address</label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-dark-light border border-gray-700 text-white focus:outline-none focus:border-primary"
                required
                placeholder="Email address"
              />
            </div>
            <div>
              <label htmlFor="company" className="block text-white mb-2">Company</label>
              <input
                type="text"
                id="company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-dark-light border border-gray-700 text-white focus:outline-none focus:border-primary"
                required
                placeholder="Company"
              />
            </div>

            {/* Website */}
            <div className="md:col-span-2">
              <label htmlFor="website" className="block text-white mb-2">Website</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                  https://
                </span>
                <input
                  type="text"
                  id="website"
                  value={formData.website}
                  onChange={handleWebsiteChange}
                  className="w-full px-4 py-3 pl-20 rounded-lg bg-dark-light border border-gray-700 text-white focus:outline-none focus:border-primary"
                  placeholder="example.com"
                  pattern="[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$"
                  title="Please enter a valid domain (e.g., example.com)"
                />
              </div>
            </div>

            {/* Industry & Annual GMV */}
            <div>
              <label htmlFor="industry" className="block text-white mb-2">Industry</label>
              <select
                id="industry"
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-dark-light border border-gray-700 text-white focus:outline-none focus:border-primary"
                required
              >
                <option value="">Select one...</option>
                <option value="retail">Retail</option>
                <option value="ecommerce">E-commerce</option>
                <option value="fashion">Fashion</option>
                <option value="electronics">Electronics</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label htmlFor="annualGMV" className="block text-white mb-2">Annual GMV</label>
              <select
                id="annualGMV"
                value={formData.annualGMV}
                onChange={(e) => setFormData({ ...formData, annualGMV: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-dark-light border border-gray-700 text-white focus:outline-none focus:border-primary"
                required
              >
                <option value="">Select one...</option>
                <option value="<1M">Less than ₹1M</option>
                <option value="1M-10M">₹1M - ₹10M</option>
                <option value="10M-50M">₹10M - ₹50M</option>
                <option value="50M+">₹50M+</option>
              </select>
            </div>

            {/* Phone & Title */}
            <div>
              <label htmlFor="phone" className="block text-white mb-2">Phone (Optional)</label>
              <input
                type="tel"
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-dark-light border border-gray-700 text-white focus:outline-none focus:border-primary"
                placeholder="Phone"
              />
            </div>
            <div>
              <label htmlFor="title" className="block text-white mb-2">Title (Optional)</label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-dark-light border border-gray-700 text-white focus:outline-none focus:border-primary"
                placeholder="E.g. CEO, CMO..."
              />
            </div>

            {/* Integration Platform & Returns Management Platform */}
            <div>
              <label htmlFor="integrationPlatform" className="block text-white mb-2">Integration Platform (Optional)</label>
              <select
                id="integrationPlatform"
                value={formData.integrationPlatform}
                onChange={(e) => setFormData({ ...formData, integrationPlatform: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-dark-light border border-gray-700 text-white focus:outline-none focus:border-primary"
              >
                <option value="">Select one...</option>
                <option value="shopify">Shopify</option>
                <option value="woocommerce">WooCommerce</option>
                <option value="magento">Magento</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div>
              <label htmlFor="returnsManagementPlatform" className="block text-white mb-2">Returns Management Platform (Optional)</label>
              <select
                id="returnsManagementPlatform"
                value={formData.returnsManagementPlatform}
                onChange={(e) => setFormData({ ...formData, returnsManagementPlatform: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-dark-light border border-gray-700 text-white focus:outline-none focus:border-primary"
              >
                <option value="">Select one...</option>
                <option value="internal">Internal System</option>
                <option value="thirdParty">Third Party</option>
                <option value="none">None</option>
              </select>
            </div>

            {/* Return Rate */}
            <div className="md:col-span-2">
              <label htmlFor="returnRate" className="block text-white mb-2">Return Rate</label>
              <select
                id="returnRate"
                value={formData.returnRate}
                onChange={(e) => setFormData({ ...formData, returnRate: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-dark-light border border-gray-700 text-white focus:outline-none focus:border-primary"
                required
              >
                <option value="">Select one...</option>
                <option value="<5%">Less than 5%</option>
                <option value="5-10%">5% - 10%</option>
                <option value="10-15%">10% - 15%</option>
                <option value=">15%">More than 15%</option>
              </select>
            </div>

            {/* Submit Button */}
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full md:w-auto px-8 py-4 bg-primary hover:bg-primary-dark text-white font-medium rounded-full transition-colors ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        </>
      )}
    </Modal>
  );
};

export default RequestDemo; 